"use client";

import type { DasamsaResponse, D10PlanetPosition, PlanetD10Strength } from "../_data";
import { STRENGTH_COLOR } from "../_data";

// ── StrengthBadge ──────────────────────────────────────────────────────────────

function StrengthBadge({ strength }: { strength: "strong" | "average" | "weak" }) {
  return (
    <span
      className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{
        background: `${STRENGTH_COLOR[strength]}1a`,
        color: STRENGTH_COLOR[strength],
      }}
    >
      {strength.charAt(0).toUpperCase() + strength.slice(1)}
    </span>
  );
}

// ── D10ChartTable ──────────────────────────────────────────────────────────────

function D10ChartTable({ positions }: { positions: D10PlanetPosition[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--ish-border)" }}>
            {["Planet", "D10 Sign", "D10 House"].map((h) => (
              <th
                key={h}
                className="text-left py-2 pr-4 font-semibold"
                style={{ color: "var(--ish-text-muted)" }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => (
            <tr
              key={p.planet_id}
              style={{ borderBottom: "1px solid var(--ish-border-faint, #f0ebe3)" }}
            >
              <td className="py-2 pr-4" style={{ color: "var(--ish-dark)" }}>
                <span className="font-medium">{p.planet_display_en}</span>
                <span className="ml-2 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
                  {p.planet_display_hi}
                </span>
              </td>
              <td className="py-2 pr-4 capitalize" style={{ color: "var(--ish-dark)" }}>
                {p.d10_sign}
              </td>
              <td className="py-2 font-semibold" style={{ color: "var(--ish-accent)" }}>
                {p.d10_house}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── StrengthTable ──────────────────────────────────────────────────────────────

function StrengthTable({ strengths }: { strengths: PlanetD10Strength[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--ish-border)" }}>
            {["Planet", "D10 Strength", "Career Domain"].map((h) => (
              <th
                key={h}
                className="text-left py-2 pr-4 font-semibold"
                style={{ color: "var(--ish-text-muted)" }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {strengths.map((s) => (
            <tr
              key={s.planet_id}
              style={{ borderBottom: "1px solid var(--ish-border-faint, #f0ebe3)" }}
            >
              <td className="py-2 pr-4 font-medium" style={{ color: "var(--ish-dark)" }}>
                {s.planet_display_en}
              </td>
              <td className="py-2 pr-4">
                <StrengthBadge strength={s.career_strength} />
                {s.is_exalted_d10 && (
                  <span
                    className="ml-1 text-[10px] font-semibold"
                    style={{ color: STRENGTH_COLOR.strong }}
                  >
                    Exalted
                  </span>
                )}
                {s.is_debilitated_d10 && (
                  <span
                    className="ml-1 text-[10px] font-semibold"
                    style={{ color: STRENGTH_COLOR.weak }}
                  >
                    Debilitated
                  </span>
                )}
              </td>
              <td className="py-2 text-[12px]" style={{ color: "var(--ish-text-muted)" }}>
                {s.karaka_domain}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── DasamsaResultsSection ──────────────────────────────────────────────────────

export function DasamsaResultsSection({ result }: { result: DasamsaResponse }) {
  return (
    <div className="mt-6 space-y-6">
      {/* D10 Lagna banner */}
      <div className="rounded-lg p-5" style={{ backgroundColor: "var(--ish-accent-light)" }}>
        <p
          className="text-[12px] font-semibold uppercase tracking-wide mb-1"
          style={{ color: "var(--ish-accent)" }}
        >
          Your Dasamsa Lagna (D10)
        </p>
        <p className="text-[22px] font-bold capitalize" style={{ color: "var(--ish-dark)" }}>
          {result.d10_lagna}
        </p>
        <p className="text-[13px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
          The D10 lagna governs your overall career temperament and the dharmic flavour of your
          profession.
        </p>
      </div>

      {/* Career indicators */}
      <div>
        <h3
          className="text-[17px] font-bold mb-3"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          Career Indicators
        </h3>
        <div className="space-y-3">
          {result.career_indicators.map((ci, i) => (
            <div key={i} className="card-sacred p-4">
              <p
                className="text-[12px] font-semibold uppercase tracking-wide mb-1"
                style={{ color: "var(--ish-accent)" }}
              >
                {ci.indicator}
              </p>
              <p className="text-[13px]" style={{ color: "var(--ish-text-muted)" }}>
                {ci.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* D10 Chart table */}
      <div>
        <h3
          className="text-[17px] font-bold mb-3"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          D10 Planet Positions
        </h3>
        <div className="card-sacred p-4">
          <D10ChartTable positions={result.d10_chart} />
        </div>
      </div>

      {/* Planet D10 strength */}
      <div>
        <h3
          className="text-[17px] font-bold mb-3"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          Planet Career Strength in D10
        </h3>
        <div className="card-sacred p-4">
          <StrengthTable strengths={result.planet_d10_strength} />
        </div>
      </div>

      {/* Predictions */}
      <div>
        <h3
          className="text-[17px] font-bold mb-3"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          D10 Career Readings
        </h3>
        <div className="space-y-3">
          {result.predictions.map((pred, i) => (
            <div
              key={i}
              className="card-sacred p-4 text-[13px]"
              style={{ color: "var(--ish-text-muted)" }}
            >
              {pred}
            </div>
          ))}
        </div>
      </div>

      <p className="text-[12px]" style={{ color: "var(--ish-text-faint)" }}>
        Engine: Swiss Ephemeris + Lahiri ayanamsa. D10 formula per Brihat Parashara Hora Shastra,
        Chapter 7.
      </p>
    </div>
  );
}
